/**
 * @fileoverview React hook for Paddle Connect operations
 * @module paddle-connect/hooks/usePaddleConnect
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPaddleConnectService } from '@modules/paddle-connect/services/PaddleConnectService';
import type {
  PaddleConnectionStatus,
  PaddleProduct,
  PaddlePrice,
  ConnectPaddleRequest,
} from '@modules/paddle-connect/types';

/**
 * Hook return type
 */
export interface UsePaddleConnectResult {
  // Status
  isConnected: boolean;
  status: PaddleConnectionStatus | null;
  loading: boolean;
  error: string | null;

  // Actions
  connect: (credentials: ConnectPaddleRequest) => Promise<void>;
  updateWebhookSecret: (environment: 'sandbox' | 'production', secret: string) => Promise<void>;
  disconnect: (environment?: 'sandbox' | 'production' | 'all') => Promise<void>;
  refreshStatus: () => Promise<void>;
  switchEnvironment: (credentials: ConnectPaddleRequest) => Promise<void>;
  setActiveEnvironment: (environment: 'sandbox' | 'production') => Promise<void>;
  refresh: () => void;

  // Products & Prices (environment-specific)
  listProducts: (environment: 'sandbox' | 'production') => Promise<PaddleProduct[]>;
  listPrices: (environment: 'sandbox' | 'production', productId?: string) => Promise<PaddlePrice[]>;
}

/**
 * React hook for Paddle Connect operations
 *
 * @param projectId - Project ID to manage Paddle for
 * @returns Paddle Connect operations and status
 *
 * @example
 * ```tsx
 * const {
 *   isConnected,
 *   status,
 *   loading,
 *   connect,
 *   disconnect,
 *   listProducts,
 * } = usePaddleConnect(projectId);
 *
 * if (loading) return <Spinner />;
 *
 * if (!isConnected) {
 *   return <ConnectPaddleForm onSubmit={connect} />;
 * }
 *
 * return <PaddleAccountStatus status={status} onDisconnect={disconnect} />;
 * ```
 */
export function usePaddleConnect(projectId: string): UsePaddleConnectResult {
  const service = useMemo(() => createPaddleConnectService(), []);
  // A new object distinguishes A -> B -> A from an old request for project A.
  const scope = useMemo(() => ({ projectId }), [projectId]);
  const activeScope = useRef(scope); activeScope.current = scope;
  const mounted = useRef(true);
  const requestSequence = useRef(0);
  const [view, setView] = useState<{ scope: typeof scope; status: PaddleConnectionStatus | null;
    loading: boolean; error: string | null }>({ scope, status: null, loading: true, error: null });
  const isCurrent = useCallback(() => mounted.current && activeScope.current === scope, [scope]);
  const publish = useCallback((patch: Partial<Omit<typeof view, 'scope'>>) => {
    if (!isCurrent()) return;
    setView(previous => ({ ...(previous.scope === scope ? previous : { scope, status: null, loading: true, error: null }), ...patch }));
  }, [scope, isCurrent]);

  const readStatus = useCallback(async (showLoading: boolean) => {
    if (!isCurrent()) return;
    const request = ++requestSequence.current;
    publish({ error: null, ...(showLoading ? { loading: true } : {}) });
    try {
      const status = await service.getStatus(projectId);
      if (request === requestSequence.current) publish({ status });
      return status;
    } catch (error) {
      // A failed observation is not evidence that a connected account disconnected.
      if (request === requestSequence.current) publish({ error: error instanceof Error ? error.message : 'Unable to read Paddle connection status' });
      throw error;
    } finally {
      if (request === requestSequence.current) publish({ loading: false });
    }
  }, [service, projectId, publish, isCurrent]);
  const refreshStatus = useCallback(async () => { await readStatus(true); }, [readStatus]);
  const refresh = useCallback(() => { void refreshStatus().catch(() => {}); }, [refreshStatus]);
  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; ++requestSequence.current; };
  }, [refresh]);

  const mutate = useCallback(async (operation: () => Promise<unknown>, showLoading = true,
    verify?: (status: PaddleConnectionStatus) => boolean) => {
    if (!isCurrent()) throw new Error('Project changed. Refresh before saving Paddle settings.');
    // Invalidate reads begun before the write, even if the write later fails.
    let request = ++requestSequence.current;
    publish({ error: null, ...(showLoading ? { loading: true } : {}) });
    try {
      await operation();
      if (!isCurrent()) throw new Error('Project changed while saving Paddle settings. Reload that project to verify the result.');
      const reading = readStatus(showLoading);
      request = requestSequence.current;
      try {
        const status = await reading;
        if (verify && (!status || !verify(status))) throw new Error('Updated connection state was not confirmed');
      }
      catch { throw new Error('Paddle settings saved, but the connection status could not be verified. Reload the page to verify the connection.'); }
      if (!isCurrent()) throw new Error('Project changed while verifying Paddle settings. Reload that project to verify the result.');
    } catch (error) {
      if (request === requestSequence.current) publish({ error: error instanceof Error ? error.message : 'Unable to save Paddle settings' });
      throw error;
    } finally {
      if (request === requestSequence.current && showLoading) publish({ loading: false });
    }
  }, [publish, isCurrent, readStatus]);
  const connect = useCallback((credentials: ConnectPaddleRequest) =>
    mutate(() => service.connect(projectId, credentials)), [mutate, service, projectId]);
  const disconnect = useCallback((environment: 'sandbox' | 'production' | 'all' = 'all') =>
    mutate(() => service.disconnect(projectId, environment)), [mutate, service, projectId]);
  const updateWebhookSecret = useCallback((environment: 'sandbox' | 'production', secret: string) =>
    // Keep the existing secret dialog mounted so it can show a save/readback error.
    mutate(() => service.updateWebhookSecret(projectId, environment, secret), false,
      status => status[environment]?.hasWebhookSecret === true), [mutate, service, projectId]);
  const switchEnvironment = useCallback((credentials: ConnectPaddleRequest) =>
    mutate(() => service.switchEnvironment(projectId, credentials)), [mutate, service, projectId]);
  const setActiveEnvironment = useCallback((environment: 'sandbox' | 'production') =>
    mutate(() => service.setActiveEnvironment(projectId, environment)), [mutate, service, projectId]);
  const listProducts = useCallback((environment: 'sandbox' | 'production') =>
    service.listProducts(projectId, environment), [service, projectId]);
  const listPrices = useCallback((environment: 'sandbox' | 'production', productId?: string) =>
    service.listPrices(projectId, environment, productId), [service, projectId]);
  // Never render the previous project's status while its replacement read is pending.
  const current = view.scope === scope ? view : { status: null, loading: true, error: null };
  return { status: current.status, loading: current.loading, error: current.error,
    isConnected: current.status?.connected ?? false, connect, disconnect,
    updateWebhookSecret, refreshStatus, switchEnvironment, setActiveEnvironment, refresh, listProducts, listPrices };
}

export default usePaddleConnect;
